import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, setToken } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { track, identify, trackSignUp, EVENTS } from "@/lib/analytics";
import { Compass, ArrowLeft, ArrowRight, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { SUPPORTED_COUNTRIES } from "@/lib/countries";

/**
 * "Try Memory Lane" — frictionless signup. Email + name + country.
 * Country lets us tailor screeners (GUIDE is US-Medicare-only) and the
 * Community Partners directory to the caregiver's actual location.
 */
export default function BetaSignup() {
    const { refresh } = useAuth();
    const navigate = useNavigate();
    const [form, setForm] = useState({ name: "", email: "", country: "US" });
    const [memoryConsent, setMemoryConsent] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    // Consent-impression event — fire ONCE when the checkbox is
    // mounted so growth reporting can compare shown vs. agreed rates.
    // Session-scoped so it doesn't fire on route re-renders.
    React.useEffect(() => {
        if (typeof window === "undefined") return;
        if (sessionStorage.getItem("ml_ga_memory_consent_shown_beta")) return;
        sessionStorage.setItem("ml_ga_memory_consent_shown_beta", "1");
        track(EVENTS.MEMORY_CONSENT_SHOWN, { form: "beta_signup" });
    }, []);

    // Fire MEMORY_CONSENT_AGREED the first time the caregiver ticks the
    // box during this session. Untick doesn't re-arm — the impression
    // "someone actively agreed at least once" is what matters.
    React.useEffect(() => {
        if (!memoryConsent) return;
        if (typeof window === "undefined") return;
        if (sessionStorage.getItem("ml_ga_memory_consent_agreed_beta")) return;
        sessionStorage.setItem("ml_ga_memory_consent_agreed_beta", "1");
        track(EVENTS.MEMORY_CONSENT_AGREED, { form: "beta_signup" });
    }, [memoryConsent]);

    const submit = async (e) => {
        e.preventDefault();
        const name = form.name.trim();
        const email = form.email.trim().toLowerCase();
        if (!name) { toast.error("What should we call you?"); return; }
        if (!/^\S+@\S+\.\S+$/.test(email)) { toast.error("That email looks off — double-check?"); return; }
        track(EVENTS.SIGNUP_STARTED, { form: "beta_page" });
        // Storage consent is required — this is the caregiver's active
        // "yes, hold onto my story" moment. We record the decision + ts
        // in localStorage as a paper trail (MHMDA / GDPR-style audit).
        if (!memoryConsent) { toast.error("Tick the box so Lane can remember your story."); return; }
        try {
            localStorage.setItem("mlcn_data_consent_v1", JSON.stringify({
                decided: true, granted: true, ts: Date.now(), version: 1,
            }));
        } catch { /* private mode — non-blocking */ }

        setSubmitting(true);
        try {
            const utmCampaign = (() => {
                try { return localStorage.getItem("ml_utm_campaign") || undefined; } catch { return undefined; }
            })();
            // Secure passwordless signup — the account is created only after
            // the caregiver verifies the emailed 6-digit code / magic link.
            await api.post("/auth/passwordless/start", {
                name, email, utm_campaign: utmCampaign, country: form.country,
            });
            try { localStorage.setItem("mlcn_country", form.country); } catch { /* private mode */ }
            toast.success("Check your email — your 6-digit code and sign-in link are on the way.");
            navigate(`/login?email=${encodeURIComponent(email)}`, { state: { codeSent: true } });
        } catch (err) {
            if (err?.response?.status === 429) {
                toast(err?.response?.data?.detail || "A code is already on its way — check your inbox.");
                navigate(`/login?email=${encodeURIComponent(email)}`, { state: { codeSent: true } });
                return;
            }
            const detail = err?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Couldn't send your code. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-cream">
            <header className="bg-cream/80 backdrop-blur-sm border-b border-[#E2E8F0]/60">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-4">
                    <Link to="/" className="flex items-center gap-2.5" data-testid="beta-brand">
                        <span className="w-9 h-9 rounded-2xl bg-teal text-white flex items-center justify-center">
                            <Compass strokeWidth={1.8} size={18} />
                        </span>
                        <span className="font-heading font-medium text-lg">Memory Lane</span>
                    </Link>
                    <Link
                        to="/"
                        className="inline-flex items-center gap-1.5 text-sm text-[#4C5551] hover:text-[#1F2624]"
                        data-testid="beta-back"
                    >
                        <ArrowLeft size={14} strokeWidth={2} /> Back
                    </Link>
                </div>
            </header>

            <main className="max-w-md mx-auto px-4 sm:px-6 py-10 md:py-16">
                <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-3xl bg-stage_late-light mb-4">
                        <Sparkles strokeWidth={1.6} size={22} className="text-stage_late-dark" />
                    </div>
                    <h1 className="text-3xl md:text-4xl font-heading font-semibold tracking-tight">A dementia care companion <span className="text-teal-dark">for the caregiver.</span></h1>
                    <p className="mt-3 text-base text-[#4C5551] leading-relaxed">
                        You carry a lot. Lane carries the details — your loved one&apos;s story, every behavior, every question — so every time you come back, you pick up right where you left off.
                    </p>
                </div>

                <form onSubmit={submit} className="ml-card p-6 space-y-4" data-testid="try-memory-lane-form">
                    <div>
                        <label className="ml-label" htmlFor="try-name">Your name</label>
                        <input
                            id="try-name"
                            type="text"
                            data-testid="try-name-input"
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            placeholder="What should we call you?"
                            autoFocus
                            className="ml-input w-full"
                            required
                        />
                    </div>
                    <div>
                        <label className="ml-label" htmlFor="try-email">Email</label>
                        <input
                            id="try-email"
                            type="email"
                            data-testid="try-email-input"
                            value={form.email}
                            onChange={(e) => setForm({ ...form, email: e.target.value })}
                            placeholder="you@example.com"
                            className="ml-input w-full"
                            required
                            autoComplete="email"
                        />
                        <p className="mt-1.5 text-xs text-[#4C5551] leading-snug">
                            Same email next time lets you pick up right where you left off — no password needed.
                        </p>
                    </div>
                    <div>
                        <label className="ml-label" htmlFor="try-country">Where are you based?</label>
                        <select
                            id="try-country"
                            data-testid="try-country-select"
                            value={form.country}
                            onChange={(e) => setForm({ ...form, country: e.target.value })}
                            className="ml-input w-full"
                        >
                            {SUPPORTED_COUNTRIES.map((c) => (
                                <option key={c.code} value={c.code}>{c.flag} {c.label}</option>
                            ))}
                        </select>
                        <p className="mt-1.5 text-xs text-[#4C5551] leading-snug">
                            We tailor screeners + partners to where you live. (US users get extra
                            tools for the Medicare GUIDE program.) You can change this anytime.
                        </p>
                    </div>
                    <label
                        className="flex items-start gap-2.5 cursor-pointer select-none"
                        data-testid="try-memory-consent-label"
                    >
                        <input
                            type="checkbox"
                            data-testid="try-memory-consent"
                            checked={memoryConsent}
                            onChange={(e) => setMemoryConsent(e.target.checked)}
                            className="mt-1 h-4 w-4 rounded border-cream-dark text-teal-dark focus:ring-2 focus:ring-teal-soft"
                            required
                        />
                        <span className="text-sm text-[#4C5551] leading-snug">
                            I&apos;m happy for Memory Lane to store my caregiving notes so Lane can remember my story.
                        </span>
                    </label>
                    <button
                        type="submit"
                        disabled={submitting}
                        data-testid="try-memory-lane-submit"
                        className="ml-btn-primary w-full !min-h-[48px] disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {submitting
                            ? <><Loader2 size={16} className="animate-spin" /> Getting you in…</>
                            : <>Start exploring <ArrowRight size={16} strokeWidth={2} /></>
                        }
                    </button>
                    <p className="text-xs text-[#6E7A74] text-center leading-snug">
                        By continuing you confirm you&apos;ve read the{" "}
                        <Link to="/terms" target="_blank" className="text-teal-dark underline">Terms</Link>{" "}
                        and understand Memory Lane is not a HIPAA-covered service.
                    </p>
                </form>

                <p className="mt-6 text-center text-sm text-[#4C5551]">
                    Already tried Memory Lane?{" "}
                    <Link to="/login" className="text-teal-dark underline" data-testid="beta-already-have">Log back in</Link>.
                </p>
            </main>
        </div>
    );
}
